/** @jsx jsx */
import { Box, Button, Tiles, Inline, jsx, Stack } from '@front10/base-elements';
import { Icon } from '../../../packages/base-elements/Icon/Icon';
import { replaceAll, useTranslation, useIsMobileView } from '@front10/utils';
import { removeInvalidCharacters } from './../../../helpers/utils';
import VisuallyHidden from '@reach/visually-hidden';
import {
  addDays,
  differenceInCalendarDays,
  isAfter,
  isBefore,
  isValid,
  parseISO,
  startOfToday,
  isSameDay,
  addMonths,
  differenceInMonths,
  differenceInDays,
  endOfMonth,
  min
} from 'date-fns';
import produce from 'immer';
import nanoid from 'nanoid';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from '../Checkbox';
import { DateRangePickerProps } from '../DateRangePicker';
import { Input } from '../Input';
import { Listbox } from '../Listbox';
import { MobileBody, MobileFilterDialog, MobileFooter, MobileHeader } from '../MobileFilterView';
import { formatDate } from '../utils';
import { DatePickerProvider } from './datePickerContext';
import { useDatePickerI18n } from './datePickerI18n';
import { Month } from './Month';
import { Dropdown } from './Popover';
import {
  END_DATE,
  FocusedInput,
  getInitialMonths,
  OnDatesChangeProps,
  START_DATE,
  useDatePicker
} from './useDatePicker';
import React from 'react';
import { useBlackoutDates } from './../../lab/DateRangePicker/useDatePicker/useBlackoutDates/useBlackoutDates';
import { OverlayContainer, useOverlayPosition, useOverlayTrigger } from '@react-aria/overlays';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { useButton } from '@react-aria/button';
import { KEY_ARROW_DOWN, KEY_ENTER, KEY_ESC, KEY_SPACE, KEY_TAB } from './helper';
import { getFirstDayOfMonth } from './useDatePicker/useDay/useDay.utils';
import { getNextActiveMonth } from './useDatePicker/useDatePicker';
import { monthLabelFormatFn, onlyMonthLabelFormatFn } from './useDatePicker/useMonth/useMonth';

interface State {
  readonly startDate: Date | null;
  readonly previousStartDate?: Date | null;
  readonly endDate: Date | null;
  readonly previousEndDate?: Date | null;
  readonly startTime?: string;
  readonly endTime?: string;
  readonly focusedInput: FocusedInput;
  readonly selectPhase: FocusedInput;
  readonly isFlexibleDates: boolean;
  readonly numberOfMonths: number;
}

function isPast(date: Date) {
  return differenceInCalendarDays(Date.now(), date) > 0;
}

function getNumberOfMonth(maxDate: Date) {
  const newMaxDate = addDays(maxDate, -1);
  let monthDiff = 0;
  const initialDate = new Date(startOfToday().getFullYear(), startOfToday().getMonth(), 1);
  let tmpEndDate = initialDate;
  while (isBefore(tmpEndDate, newMaxDate) || isSameDay(tmpEndDate, newMaxDate)) {
    monthDiff++;
    tmpEndDate = addMonths(initialDate, monthDiff);
  }
  return monthDiff;
}

function DatePickerCmp(props: DateRangePickerProps, ref: any) {
  const {
    fetchBlackoutDates: fetchBlackoutDatesFn,
    blackoutDatesIntegration,
    defaultValue,
    timeOptions,
    flexibleDates,
    maxDate: maxDateProp,
    minDate: minDateProp,
    maxRange,
    minRange,
    selectedOrigin,
    selectedDestination,
    singleSelection,
    numberOfMonths: numberOfMonthsProp,
    enableAutomaticStatusNavBarButtons,
    dayFormat,
    dateFormat: dateFormatProp,
    dateFormatOnEdit: dateFormatOnEditProp,
    flightType,
    columns,
    showTimePickers,
    labels,
    startDateHasError,
    UNSTABLE_hideDisabledInput,
    endDateHasError,
    startDateErrorMessage,
    startDateTimeErrorMessage,
    endDateErrorMessage,
    endDateTimeErrorMessage,
    showFlexibleDates,
    activeTab,
    currentTab,
    setValue,
    getValues,
    setSelectedDates,
    onChange,
    onKeyDownHandleEnterAction,
    trigger,
    allowHoverStyleInRange = true,
    customInputStyle,
    shouldFlip,
    ...rest
  } = props;
  const translate = useTranslation();
  const isMobile = useIsMobileView();
  const fetchBlackoutDates = fetchBlackoutDatesFn ? fetchBlackoutDatesFn : null;
  const minDate =
    typeof minDateProp === 'string'
      ? parseISO(minDateProp)
      : addDays(startOfToday(), minDateProp ?? 0);
  let maxDate: Date | undefined = undefined;
  if (maxDateProp != null) {
    maxDate =
      typeof maxDateProp === 'string'
        ? parseISO(maxDateProp)
        : addDays(startOfToday(), maxDateProp - 1);
  }
  const [state, setState] = useState<State>({
    startDate: defaultValue?.start ?? null,
    previousStartDate: defaultValue?.start ?? null,
    endDate: defaultValue?.end ?? null,
    previousEndDate: defaultValue?.end ?? null,
    startTime: defaultValue?.startTime ?? timeOptions?.[0]?.value,
    endTime: defaultValue?.endTime ?? timeOptions?.[0]?.value,
    focusedInput: null,
    selectPhase: null,
    isFlexibleDates: flexibleDates ?? false,
    numberOfMonths: isMobile ? (maxDate ? getNumberOfMonth(maxDate) : 6) : 6
  });

  const overlayState = useOverlayTriggerState({});

  const { getMinBlackoutDate, getMaxBlackoutDate_ } = useBlackoutDates({
    startDate: state.startDate,
    maxRange
  });

  React.useEffect(() => {
    if (blackoutDatesIntegration === true && fetchBlackoutDates) {
      if (singleSelection) {
        if (fetchBlackoutDates?.isValidSelectedDepartureDate !== undefined) {
          if (fetchBlackoutDates?.isValidSelectedDepartureDate === false) {
            if (setValue) {
              setValue('dates', {});
            }
            setState({ ...state, startDate: null, endDate: null });
          } else {
            if (setValue && getValues && state.startDate) {
              setValue('dates', { start: state.startDate, end: null });
              setState({ ...state, startDate: state.startDate, endDate: null });
            }
          }
        }
      } else {
        if (
          fetchBlackoutDates?.isValidSelectedDepartureDate !== undefined &&
          fetchBlackoutDates?.isValidSelectedDepartureDate === false &&
          fetchBlackoutDates?.isValidSelectedReturnDate !== undefined &&
          fetchBlackoutDates?.isValidSelectedReturnDate === false
        ) {
          setState({ ...state, startDate: null, endDate: null });
        } else if (
          fetchBlackoutDates?.isValidSelectedDepartureDate !== undefined &&
          fetchBlackoutDates?.isValidSelectedDepartureDate === false
        ) {
          setState({ ...state, startDate: null });
        } else if (
          fetchBlackoutDates?.isValidSelectedReturnDate !== undefined &&
          fetchBlackoutDates?.isValidSelectedReturnDate === false
        ) {
          setState({ ...state, endDate: null });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBlackoutDates]);

  React.useEffect(() => {
    if (blackoutDatesIntegration === true) {
      if (selectedOrigin?.value === '') {
        setState({ ...state, startDate: null, endDate: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrigin]);

  React.useEffect(() => {
    if (blackoutDatesIntegration === true) {
      if (selectedDestination?.value === '') {
        setState({ ...state, startDate: null, endDate: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDestination]);

  const monthRef = useRef<HTMLDivElement>(null);
  const monthsToShow = numberOfMonthsProp ?? 2;
  const numberOfMonths = isMobile ? state.numberOfMonths : monthsToShow;

  const previousMonthButtonRef = useRef<HTMLButtonElement>(null);
  const nextMonthButtonRef = useRef<HTMLButtonElement>(null);
  const clearBtnRef = useRef<HTMLButtonElement>(null);
  const doneBtnRef = useRef<HTMLButtonElement>(null);

  const triggerRef = useRef<any>(null);

  useEffect(() => {
    setSelectedDates?.({ startDate: state.startDate, endDate: state.endDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDisabledPreviousBtn = (month: number, year: number) => {
    if (enableAutomaticStatusNavBarButtons || enableAutomaticStatusNavBarButtons === undefined) {
      if (minDateProp !== undefined) {
        const minDate_ =
          blackoutDatesIntegration === true ? min([minBookingDate ?? minDate, minDate]) : minDate;
        if (year < minDate_.getFullYear()) {
          return true;
        } else if (year === minDate_.getFullYear()) {
          if (minDate_.getMonth() >= month) {
            return true;
          }
        }
      }
      return false;
    }
    return false;
  };

  /**
   * Increase numbered months from 1 to 12
   * @param month
   * @param year
   * @param amount
   * @return {month, year}
   */
  const increaseMonthYear = (
    month: number,
    year: number,
    amount: number
  ): { month: number; year: number } => {
    let nextMonth = month + amount;
    let yearInc = Math.floor((nextMonth > 12 ? nextMonth : 0) / 12);
    nextMonth = nextMonth % 12;
    nextMonth = nextMonth === 0 ? 12 : nextMonth;
    return { month: nextMonth, year: year + yearInc };
  };

  const isDisabledNextBtn = (month: number, year: number) => {
    const maxDate_ = blackoutDatesIntegration === true ? maxBookingDate : maxDate;
    if (enableAutomaticStatusNavBarButtons || !maxDate_) {
      return false;
    }
    const maxDateEpoch = maxDate_.getTime() / 1000;
    const rightMonthYear = increaseMonthYear(month, year, 2);
    const rightMonthLastDay = new Date(rightMonthYear.year, rightMonthYear.month, 0);
    const rightMonthLastDayEpoch = rightMonthLastDay.getTime() / 1000;
    return rightMonthLastDayEpoch >= maxDateEpoch - 24 * 3600;
  };

  const handleScroll = (e: any) => {
    if (maxDate) {
      let totalMonthsToShow = differenceInMonths(maxDate, startOfToday());
      if (differenceInDays(maxDate, endOfMonth(addMonths(startOfToday(), totalMonthsToShow))) > 0) {
        totalMonthsToShow++;
      }
      if (totalMonthsToShow > state.numberOfMonths) {
        const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
        if (bottom) {
          if (totalMonthsToShow - state.numberOfMonths >= 3) {
            setState({ ...state, numberOfMonths: state.numberOfMonths + 3 });
          } else {
            setState({ ...state, numberOfMonths: totalMonthsToShow - state.numberOfMonths });
          }
        }
      }
    } else {
      const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
      if (bottom) {
        setState({ ...state, numberOfMonths: state.numberOfMonths + 3 });
      }
    }
  };

  const { parse = parseISO, format = formatDate } = useDatePickerI18n();
  const dateFormat = dateFormatProp ?? 'yyyy-MM-dd';
  const dateFormatOnEdit = dateFormatOnEditProp ?? dateFormat;
  const labelDateFormat = removeInvalidCharacters(replaceAll(dateFormatOnEdit, ' ', '-'));
  const dateFormatToDescribe = translate(`date-format-${labelDateFormat}`, labelDateFormat);
  const getFormat = (isOnEdit: boolean) => (isOnEdit ? dateFormatOnEdit : dateFormat);

  function getEndDate() {
    if (singleSelection === null) {
      return null;
    } else {
      if (state.endDate === null) {
        return state.startDate;
      } else {
        return state.endDate;
      }
    }
  }

  const maxBookingDate = getMaxBlackoutDate_(
    maxDate,
    blackoutDatesIntegration,
    fetchBlackoutDates,
    state.selectPhase
  );

  const minBookingDate = getMinBlackoutDate(
    minDate,
    blackoutDatesIntegration,
    fetchBlackoutDates,
    state.selectPhase
  );

  const mobileMonths = useMemo(
    () =>
      getInitialMonths(
        numberOfMonths,
        minBookingDate && startOfToday() < minBookingDate
          ? state.startDate ?? minDate
          : startOfToday()
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [numberOfMonths]
  );

  React.useEffect(() => {
    if (state.selectPhase !== null && isMobile) {
      setState({
        ...state,
        numberOfMonths: getNumberOfMonth((maxBookingDate ?? maxDate) as any)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectPhase]);

  const {
    firstDayOfWeek,
    activeMonths,
    isDateSelected,
    isDateHovered,
    isFirstOrLastSelectedDate,
    isStartDate,
    isEndDate,
    isDateBlocked,
    isDateFocused,
    focusedDate,
    onDateHover,
    onDateSelect,
    onDateFocus,
    goToDate,
    onResetDates,
    resetDatesKeepingFocus,
    goToNextMonthsByOneMonth,
    goToPreviousMonthsByOneMonth,
    getMinimumEnabledDate
  } = useDatePicker({
    startDate: state.startDate,
    endDate: getEndDate(),
    focusedInput: state.focusedInput,
    selectPhase: state.selectPhase,
    onDatesChange: handleDateChange,
    exactMinBookingDays: singleSelection,
    numberOfMonths,
    maxBookingDate,
    minBookingDate,
    minBookingDays: minRange ? minRange + 1 : undefined,
    blackoutDatesIntegration,
    initialVisibleMonth: minDate,
    isDateBlocked: isPast,
    fetchUnavailableDates: blackoutDatesIntegration ? fetchBlackoutDates : null
  });

  const popoverRef = useRef<HTMLDivElement>(null);

  const months = isMobile ? mobileMonths : activeMonths;
  const [isDialogOpen, setDialogOpen] = useState(false);
  const startRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLInputElement>(null);
  const [departureDescriptionId] = useState(() => nanoid());
  const [returnDescriptionId] = useState(() => nanoid());
  const [activeDone, setActiveDone] = useState(false);
  const [makeClearAction, setMakeClearAction] = React.useState(false);

  const dismiss = () => {
    setDialogOpen(false);
    setState({ ...state, focusedInput: null, selectPhase: null });
    setActiveDone(false);
  };

  const save = () => {
    setDialogOpen(false);
  };

  function setStartInput(date: Date | null | undefined, isOnEdit: boolean) {
    if (startRef.current) {
      startRef.current.value = date
        ? format(date, getFormat(isOnEdit))
        : translate(labels?.departurePlaceholder || 'cmp-booking-departure-placeholder', 'From');
    }
  }

  function setEndInput(date: Date | null | undefined, isOnEdit: boolean) {
    if (endRef.current) {
      endRef.current.value =
        date && !singleSelection
          ? format(date, getFormat(isOnEdit))
          : translate(labels?.returnPlaceholder || 'cmp-booking-return-placeholder', 'To');
    }
  }

  function handleDateChange(data: OnDatesChangeProps & { shouldUpdate?: boolean }) {
    let { startDate, endDate } = data;
    if (
      (startDate !== null && startDate !== undefined) ||
      (endDate !== null && endDate !== undefined)
    ) {
      setMakeClearAction(false);
    }
    if (data.startDate && data.endDate && isAfter(data.startDate, data.endDate)) {
      endDate = data.startDate;
    }

    // If the selected start date makes the resulting range
    // to violate the maxRange prop reduce the end date to
    // the maximum value that still makes the range valid.
    if (data.selectPhase === END_DATE && maxRange && startDate && endDate) {
      const maxAllowedDate = addDays(startDate, maxRange - 1);
      if (isBefore(maxAllowedDate, endDate)) {
        endDate = maxAllowedDate;
      }
    }

    setState((oldState) =>
      produce(oldState, (draft) => {
        draft.focusedInput = data.focusedInput ?? START_DATE;
        draft.selectPhase = data.selectPhase ?? START_DATE;
        draft.startDate = startDate;
        draft.endDate = endDate;
      })
    );

    const shouldUpdate = data.shouldUpdate ?? true;
    if (shouldUpdate) {
      let showFormat: boolean;
      if (data.selectPhase === START_DATE) {
        showFormat = true;
      } else if (data.selectPhase === null) {
        showFormat = true;
      } else {
        showFormat = false;
      }
      setStartInput(startDate, showFormat);
      setEndInput(endDate, data.selectPhase === END_DATE);
    }
  }

  useEffect(() => {
    if (state.selectPhase === null) {
      setStartInput(state.startDate, false);
      setEndInput(state.endDate, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectPhase, singleSelection]);

  useEffect(() => {
    if (blackoutDatesIntegration === true) {
      if (
        singleSelection &&
        fetchBlackoutDates?.isValidSelectedDepartureDate !== undefined &&
        fetchBlackoutDates?.isValidSelectedDepartureDate === false
      ) {
        setState({ ...state, startDate: null, endDate: null });
      }
    } else {
      if (state.endDate && singleSelection) {
        setState({ ...state, endDate: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleSelection]);

  useEffect(() => {
    onChange?.({
      start: state.startDate ?? undefined,
      end: state.endDate && !singleSelection ? state.endDate : undefined,
      startTime: state.startTime,
      endTime: state.endTime,
      isFlexibleDates: state.isFlexibleDates
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.endDate,
    state.endTime,
    state.startDate,
    state.startTime,
    singleSelection,
    state.isFlexibleDates,
    state.numberOfMonths
  ]);

  const getDisabledReturnStatus = () => {
    if (blackoutDatesIntegration) {
      if (
        fetchBlackoutDates !== null &&
        fetchBlackoutDates !== undefined &&
        selectedOrigin &&
        selectedOrigin?.value !== '' &&
        selectedDestination &&
        selectedDestination?.value !== '' &&
        flightType !== 'ow'
      ) {
        return false;
      } else {
        return true;
      }
    }
    return singleSelection;
  };

  const getDisabledDepartureStatus = () => {
    if (blackoutDatesIntegration) {
      if (
        fetchBlackoutDates !== null &&
        fetchBlackoutDates !== undefined &&
        selectedOrigin &&
        selectedOrigin?.value !== '' &&
        selectedDestination &&
        selectedDestination?.value !== ''
      ) {
        return false;
      } else {
        return true;
      }
    }
    return false;
  };

  function pressEscapeKey() {
    setState({ ...state, focusedInput: null });
    overlayState.close();
  }

  function handleTabForGoIntoCalendar(cond: boolean) {
    if (cond) {
      if (
        state.focusedInput === START_DATE &&
        state.startDate &&
        months.some((m) => m.month === state.startDate?.getMonth())
      ) {
        onDateFocus(state.startDate);
      } else if (
        state.focusedInput === START_DATE &&
        (!state.startDate ||
          (state.startDate && !months.some((m) => m.month === state.startDate?.getMonth())))
      ) {
        onDateFocus(getFirstDayOfMonth(months[0].date, (date) => !isDateBlocked(date)));
      } else if (
        state.focusedInput === END_DATE &&
        state.endDate &&
        months.some((m) => m.month === state.endDate?.getMonth())
      ) {
        onDateFocus(state.endDate);
      } else if (
        state.focusedInput === END_DATE &&
        (!state.endDate ||
          (state.endDate && !months.some((m) => m.month === state.endDate?.getMonth())))
      ) {
        onDateFocus(getFirstDayOfMonth(months[0].date, (date) => !isDateBlocked(date)));
      }
    }
  }

  // console.log('triggerProps', triggerProps);
  // useButton ensures that focus management is handled correctly,
  // across all browsers. Focus is restored to the button once the
  // popover closes.
  const { buttonProps } = useButton({ elementType: 'input' }, triggerRef);

  const showNextMonthA11y = useRef(false);
  const showPreviousMonthA11y = useRef(false);
  const monthsLabels = activeMonths.map((m) => monthLabelFormatFn(new Date(m.year, m.month)));
  const prevMonthForA11y = useMemo(() => addMonths(activeMonths[0].date, -1), [activeMonths]);

  const [renderA11yMessage, setRenderA11yMessage] = React.useState<false | true | undefined>(
    undefined
  );

  const refreshA11yMessage = React.useCallback(() => {
    setRenderA11yMessage(false);
    setTimeout(() => {
      setRenderA11yMessage(true);
    }, 300);
  }, []);

  React.useEffect(() => {
    setRenderA11yMessage(false);
    setTimeout(() => {
      setRenderA11yMessage(true);
    }, 300);
  }, [activeMonths]);

  const a11yMessageForNavigation = (
    <div
      key={months.map((m) => m.month + '_' + m.year).join('-')}
      className="fr-sr-only"
      aria-live="polite"
    >
      {renderA11yMessage ? (
        <React.Fragment>
          {` ${translate('cmp-datepicker-show-month', 'Current month')} ${monthsLabels[0]} `}
          {` ${
            showPreviousMonthA11y.current &&
            minDate &&
            (prevMonthForA11y.getFullYear() > minDate.getFullYear() ||
              (prevMonthForA11y.getFullYear() === minDate.getFullYear() &&
                prevMonthForA11y.getMonth() >= minDate.getMonth()))
              ? ` ${translate(
                  'cmp-datepicker-prev-month',
                  'Previous month'
                )}, ${onlyMonthLabelFormatFn(addMonths(activeMonths[0].date, -1))}`
              : ''
          } `}
          {` ${
            showNextMonthA11y.current
              ? ` ${translate('cmp-datepicker-next-month', 'Next month')}, ${onlyMonthLabelFormatFn(
                  activeMonths[1].date
                )} `
              : ''
          } `}
          {isDisabledNextBtn(
            monthsToShow === 2 ? months[0].month : months[monthsToShow - 1].month,
            monthsToShow === 2 ? months[0].year : months[monthsToShow - 1].year
          )
            ? translate('cmp-datepicker-next-month-button-disabled', 'Next month button disabled')
            : ''}
          {isDisabledPreviousBtn(months[0].month, months[0].year)
            ? translate(
                'cmp-datepicker-previous-month-button-disabled',
                'Previous month button disabled'
              )
            : ''}
        </React.Fragment>
      ) : null}
    </div>
  );

  const NewStartErrorMessage =
    startDateErrorMessage &&
    React.cloneElement(startDateErrorMessage as React.ReactElement, {
      id: `screenReaderMessageComboboxState-${departureDescriptionId}`
    });

  const StartDateInput = (
    <React.Fragment>
      <Input
        {...buttonProps}
        aria-haspopup="dialog"
        aria-expanded={overlayState.isOpen && state.focusedInput === START_DATE}
        readOnly
        aria-readonly="true"
        inputMode="none"
        label={translate(labels?.departure ?? 'cmp-booking-departure', 'Departure')}
        aria-describedby={`screenReaderMessageComboboxState-${departureDescriptionId} ${departureDescriptionId}`}
        value={
          state.startDate
            ? format(state.startDate, dateFormat)
            : translate(labels?.departurePlaceholder ?? 'cmp-booking-departure-placeholder', 'From')
        }
        // placeholder={labels?.departurePlaceholder ?? 'From'}
        state={state.focusedInput === START_DATE ? 'focus' : undefined}
        sx={{
          textAlign: 'start'
        }}
        customInputStyle={{
          '&[data-state=focus]': { boxShadow: overlayState.isOpen ? 'none' : 'focus' },
          ...customInputStyle
        }}
        rightIcon="calendar-material"
        rightIconSize={20}
        disabled={getDisabledDepartureStatus()}
        ref={startRef}
        hasError={startDateHasError}
        errorMessage={NewStartErrorMessage}
        active={Boolean(state.startDate)}
        onChange={(evt) => {
          const isEmpty = evt.target.value.trim() === '';
          const date = isEmpty ? null : parse(evt.target.value, dateFormatOnEdit);
          // FIXME: Remove bypassing null check
          if (isEmpty || (isValid(date) && !isDateBlocked(date!!))) {
            handleDateChange({
              startDate: date,
              endDate: state.endDate,
              focusedInput: START_DATE,
              selectPhase: START_DATE,
              shouldUpdate: false
            });
          }
        }}
        onClick={(evt) => {
          // REVIEW: Open dialog when button is disabled
          if (isMobile) {
            setDialogOpen(true);
          } else {
            setState({ ...state, focusedInput: START_DATE, selectPhase: START_DATE });
            overlayState.open();
          }
        }}
        onFocus={() => {
          setStartInput(state.startDate, true);

          if (isMobile) {
            setState({ ...state, focusedInput: START_DATE, selectPhase: START_DATE });
          }

          if (state.startDate !== null) {
            goToDate(state.startDate);
          } else {
            const date = getMinBlackoutDate(
              minDate,
              blackoutDatesIntegration,
              fetchBlackoutDates,
              START_DATE
            );
            if (blackoutDatesIntegration === true && date) {
              goToDate(date);
            } else {
              goToDate(minDate);
            }
          }
        }}
        onBlur={() => {
          setStartInput(state.startDate, false);
        }}
        onKeyDown={(e) => {
          if (e.keyCode === KEY_ARROW_DOWN || e.keyCode === KEY_SPACE) {
            e.stopPropagation();
            e.preventDefault();
            setState({ ...state, focusedInput: START_DATE, selectPhase: START_DATE });
            setTimeout(() => nextMonthButtonRef.current?.focus(), 100);
            if (state.startDate) {
              onDateFocus(state.startDate);
            } else if (state.endDate) {
              onDateFocus(state.endDate);
            } else {
              onDateFocus(getFirstDayOfMonth(months[0].date, (date) => !isDateBlocked(date)));
            }
            refreshA11yMessage();
            overlayState.open();
          }
          if (e.keyCode === KEY_ESC) {
            pressEscapeKey();
          }
          if (e.keyCode === KEY_TAB) {
            if (e.shiftKey) {
              setState({ ...state, focusedInput: null, selectPhase: null });
            } else {
              setState({ ...state, focusedInput: END_DATE, selectPhase: END_DATE });
            }
          }
          if (onKeyDownHandleEnterAction) {
            onKeyDownHandleEnterAction(e);
          }
        }}
      />
      <VisuallyHidden id={departureDescriptionId}>
        {translate(
          'cmp-datepicker-departure-description',
          'Type the departure date in the format {format}',
          { format: dateFormatToDescribe }
        )}
        {state.startDate
          ? translate('cmp-datepicker-departure-start-day-selected', ' Selected {date}', {
              date: formatDate(state.startDate, labelDateFormat)
            })
          : translate('cmp-datepicker-departure-no-day-selected', ' No date selected')}
      </VisuallyHidden>
      {showTimePickers && (
        <Stack gap={1}>
          <Listbox
            options={timeOptions ?? []}
            onChange={(value) => {
              setState({ ...state, startTime: value });
              setValue?.('dates', { ...getValues?.().dates, startTime: value });
            }}
            hasError={startDateHasError}
            label={translate('cmp-booking-timepicker-label', 'Time')}
            layout="closed"
            defaultValue={state.startTime ?? timeOptions?.[0].value}
            ariaDescribedBy={`screenReaderMessageComboboxState-${departureDescriptionId}`}
          />
          {startDateHasError && startDateTimeErrorMessage}
        </Stack>
      )}
    </React.Fragment>
  );

  const NewEndErrorMessage =
    endDateErrorMessage &&
    React.cloneElement(endDateErrorMessage as React.ReactElement, {
      id: `screenReaderMessageComboboxState-${returnDescriptionId}`
    });

  const EndDateInput = (
    <React.Fragment>
      <Box
        sx={{
          display: singleSelection && UNSTABLE_hideDisabledInput ? 'none' : 'inline'
        }}
      >
        <Input
          {...buttonProps}
          aria-haspopup="dialog"
          aria-expanded={overlayState.isOpen && state.focusedInput === START_DATE}
          readOnly
          aria-readonly="true"
          inputMode="none"
          label={translate(labels?.return ?? 'cmp-booking-return', 'Return')}
          aria-describedby={`screenReaderMessageComboboxState-${
            startDateHasError && !state?.endDate ? departureDescriptionId : returnDescriptionId
          } ${returnDescriptionId}`}
          // placeholder={labels?.returnPlaceholder ?? 'To'}
          value={
            state.endDate && !singleSelection
              ? format(state.endDate, dateFormat)
              : translate(labels?.returnPlaceholder ?? 'cmp-booking-return-placeholder', 'To')
          }
          state={state.focusedInput === END_DATE ? 'focus' : undefined}
          sx={{
            textAlign: 'start'
          }}
          customInputStyle={{
            '&[data-state=focus]': { boxShadow: overlayState.isOpen ? 'none' : 'focus' },
            ...customInputStyle
          }}
          rightIcon="calendar-material"
          rightIconSize={20}
          ref={endRef}
          hasError={endDateHasError}
          errorMessage={NewEndErrorMessage}
          disabled={getDisabledReturnStatus()}
          active={Boolean(state.endDate)}
          onChange={(evt) => {
            const isEmpty = evt.target.value.trim() === '';
            const date = isEmpty ? null : parse(evt.target.value, dateFormatOnEdit);
            // FIXME: Remove bypassing null check
            if (
              isEmpty ||
              (isValid(date) &&
                !isDateBlocked(date!!) &&
                (!state.startDate || differenceInCalendarDays(date!!, state.startDate) >= 0))
            ) {
              handleDateChange({
                startDate: state.startDate,
                endDate: date,
                focusedInput: END_DATE,
                selectPhase: END_DATE,
                shouldUpdate: false
              });
            }
          }}
          onClick={() => {
            if (isMobile) {
              setDialogOpen(true);
            } else {
              overlayState.open();
              setState({ ...state, focusedInput: END_DATE, selectPhase: END_DATE });
            }
          }}
          onFocus={() => {
            setEndInput(state.endDate, true);

            if (isMobile) {
              setState({ ...state, focusedInput: END_DATE, selectPhase: END_DATE });
            }

            if (state.endDate) {
              goToDate(state.endDate);
            } else {
              const date = getMinBlackoutDate(
                minDate,
                blackoutDatesIntegration,
                fetchBlackoutDates,
                END_DATE
              );
              if (blackoutDatesIntegration === true && date) {
                goToDate(date);
              } else {
                goToDate(minDate);
              }
            }
          }}
          onBlur={() => {
            setEndInput(state.endDate, false);
          }}
          onKeyDown={(e) => {
            if (e.keyCode === KEY_ARROW_DOWN || e.keyCode === KEY_SPACE) {
              e.preventDefault();
              e.stopPropagation();
              setState({ ...state, focusedInput: END_DATE, selectPhase: END_DATE });
              setTimeout(() => {
                if (previousMonthButtonRef.current && !previousMonthButtonRef.current.disabled) {
                  previousMonthButtonRef.current?.focus();
                } else {
                  nextMonthButtonRef.current?.focus();
                }
              }, 100);
              if (state.endDate) {
                onDateFocus(state.endDate);
              } else if (state.startDate) {
                onDateFocus(state.startDate);
              } else {
                onDateFocus(getFirstDayOfMonth(months[0].date, (date) => !isDateBlocked(date)));
              }
              refreshA11yMessage();
              overlayState.open();
            }
            if (e.keyCode === KEY_ESC) {
              pressEscapeKey();
            }
            if (e.keyCode === KEY_TAB) {
              if (e.shiftKey) {
                setState({ ...state, focusedInput: START_DATE, selectPhase: START_DATE });
              } else {
                setState({ ...state, focusedInput: null, selectPhase: null });
              }
            }
            if (onKeyDownHandleEnterAction) {
              onKeyDownHandleEnterAction(e);
            }
          }}
        />
        <VisuallyHidden id={returnDescriptionId}>
          {translate(
            'cmp-datepicker-return-description',
            'Type the return date in the format {format}',
            { format: dateFormatToDescribe }
          )}
          {state.endDate
            ? translate('cmp-datepicker-departure-end-day-selected', ' Selected {date}', {
                date: formatDate(state.endDate, labelDateFormat)
              })
            : translate('cmp-datepicker-departure-no-day-selected', ' No date selected')}
        </VisuallyHidden>
      </Box>
      {showTimePickers && (
        <Stack gap={1}>
          <Listbox
            options={timeOptions ?? []}
            onChange={(value) => {
              setState({ ...state, endTime: value });
              setValue?.('dates', { ...getValues?.().dates, endTime: value });
            }}
            hasError={endDateHasError}
            label={translate('cmp-booking-timepicker-label', 'Time')}
            layout="closed"
            defaultValue={state.endTime ?? timeOptions?.[0].value}
            ariaDescribedBy={`screenReaderMessageComboboxState-${returnDescriptionId}`}
          />
          {endDateHasError && endDateTimeErrorMessage}
        </Stack>
      )}
    </React.Fragment>
  );

  const inputs = (
    <Tiles
      gap={2}
      sx={{
        height: '100%',
        alignContent: 'space-evenly',
        variant: 'layout.datepickerStyles.inputsContainer'
      }}
      columns={columns ?? (showTimePickers ? [2, 2, 4] : 2)}
    >
      {StartDateInput}
      {EndDateInput}
    </Tiles>
  );

  const footer = (
    <Inline
      p="2"
      variant="separators.default"
      align="space-between"
      onKeyDown={(evt) => {
        if (evt.keyCode === KEY_ESC) {
          pressEscapeKey();
        }
      }}
    >
      {showFlexibleDates ? (
        <Checkbox
          role="checkbox"
          aria-checked={state.isFlexibleDates}
          checked={state.isFlexibleDates}
          label={translate('cmp-booking-flexible-dates-label', 'Flexible dates')}
          onChange={(evt) => {
            setState({
              ...state,
              isFlexibleDates: evt.target.checked
            });
          }}
          onKeyDown={(evt) => {
            handleTabForGoIntoCalendar(evt.keyCode === KEY_TAB && evt.shiftKey && !focusedDate);
          }}
        />
      ) : null}
      <Inline align="end" sx={{ flex: '1' }}>
        <Button
          ref={clearBtnRef}
          variant="text"
          disabled={state.startDate === null && state.endDate === null}
          onClick={() => {
            setMakeClearAction(true);
            setState(
              produce(state, (draft) => {
                draft.startDate = null;
                draft.previousStartDate = null;
                draft.endDate = null;
                draft.previousEndDate = null;
                draft.focusedInput = START_DATE;
                draft.selectPhase = START_DATE;
              })
            );
            setActiveDone(true);
            setSelectedDates?.({ startDate: null, endDate: null });
            onResetDates();
            const date = getMinBlackoutDate(
              minDate,
              blackoutDatesIntegration,
              fetchBlackoutDates,
              START_DATE
            );
            if (blackoutDatesIntegration === true && date) {
              goToDate(date);
            } else {
              goToDate(minDate);
            }
          }}
          onKeyDown={(evt) => {
            handleTabForGoIntoCalendar(
              evt.keyCode === KEY_TAB && evt.shiftKey && !focusedDate && !showFlexibleDates
            );
            if (evt.keyCode === KEY_SPACE || evt.keyCode === KEY_ENTER) {
              evt.preventDefault();
              setMakeClearAction(true);
              setState(
                produce(state, (draft) => {
                  draft.startDate = null;
                  draft.previousStartDate = null;
                  draft.endDate = null;
                  draft.previousEndDate = null;
                  draft.selectPhase = START_DATE;
                })
              );
              setActiveDone(true);
              setSelectedDates?.({ startDate: null, endDate: null });
              resetDatesKeepingFocus();
              const date = getMinBlackoutDate(
                minDate,
                blackoutDatesIntegration,
                fetchBlackoutDates,
                START_DATE
              );
              if (blackoutDatesIntegration === true && date) {
                goToDate(date);
                onDateFocus(date);
              } else {
                goToDate(minDate);
                onDateFocus(minDate);
              }
            }
          }}
        >
          {translate('cmp-booking-datepicker-clear-label', 'Clear')}
        </Button>
        <Button
          ref={doneBtnRef}
          variant="text"
          disabled={
            makeClearAction
              ? true
              : activeDone
              ? false
              : singleSelection
              ? state.startDate === null
              : state.startDate === null || state.endDate === null
          }
          onClick={() => {
            if (isMobile) {
              save();
            } else {
              setStartInput(state.startDate, false);
              setEndInput(state.endDate, false);
              setSelectedDates?.({ startDate: state.startDate, endDate: state.endDate });
              setState((state) =>
                produce(state, (draft) => {
                  draft.focusedInput = null;
                  draft.selectPhase = null;
                  draft.previousStartDate = state.startDate;
                  draft.previousEndDate = state.endDate;
                })
              );
              // popup.ref.current?.close();
              overlayState.close();
            }
            setActiveDone(false);
            if (trigger) {
              trigger('dates');
            }
          }}
          onKeyDown={(evt) => {
            if (evt.keyCode === KEY_SPACE || evt.keyCode === KEY_ENTER) {
              evt.preventDefault();
              doneBtnRef.current?.click();
            }
          }}
        >
          {translate('cmp-booking-datepicker-done-label', 'Done')}
        </Button>
      </Inline>
    </Inline>
  );

  const navigation = (
    <Inline
      align="space-between"
      pr="4"
      pl="4"
      sx={{
        position: 'absolute',
        width: '100%',
        top: '20px',
        variant: 'layout.datepickerStyles.navigation'
      }}
      onKeyDown={(evt) => {
        if (evt.keyCode === KEY_ESC) {
          pressEscapeKey();
        }
      }}
    >
      {a11yMessageForNavigation}
      <div className="calendar-header-button-container">
        <div className="cmp-datepicker-previous-button">
          <Button
            variant="iconRounded"
            ref={previousMonthButtonRef}
            aria-label={translate('cmp-datepicker-button-previous-label', 'Previous Month')}
            disabled={isDisabledPreviousBtn(months[0].month, months[0].year)}
            aria-disabled={isDisabledPreviousBtn(months[0].month, months[0].year)}
            onClick={() => {
              goToPreviousMonthsByOneMonth();
              if (state.selectPhase === START_DATE) {
                setStartInput(state.startDate, true);
              } else if (state.selectPhase === END_DATE) {
                setEndInput(state.endDate, true);
              }
              // Focus next month button when previous month is not enabled
              const prevMonth = getNextActiveMonth(months, numberOfMonths, -1, 1);
              if (isDisabledPreviousBtn(prevMonth[0].month, prevMonth[0].year)) {
                nextMonthButtonRef.current?.focus();
              }

              showPreviousMonthA11y.current = true;
              showNextMonthA11y.current = false;
            }}
            onKeyDown={(evt) => {
              if (evt.keyCode === KEY_SPACE || evt.keyCode === KEY_ENTER) {
                evt.preventDefault();
                previousMonthButtonRef.current?.click();
                refreshA11yMessage();
              }
            }}
          >
            <Icon
              icon="left-chevron"
              size={14}
              fill="text"
              style={{ stroke: '#4a4a4a', strokeWidth: '120px' }}
            />
          </Button>
        </div>
        <div className="cmp-datepicker-next-button">
          <Button
            ref={nextMonthButtonRef}
            variant="iconRounded"
            disabled={isDisabledNextBtn(
              monthsToShow === 2 ? months[0].month : months[monthsToShow - 1].month,
              monthsToShow === 2 ? months[0].year : months[monthsToShow - 1].year
            )}
            aria-disabled={isDisabledNextBtn(
              monthsToShow === 2 ? months[0].month : months[monthsToShow - 1].month,
              monthsToShow === 2 ? months[0].year : months[monthsToShow - 1].year
            )}
            aria-label={translate('cmp-datepicker-button-next-label', 'Next Month')}
            onClick={() => {
              goToNextMonthsByOneMonth();
              if (state.selectPhase === START_DATE) {
                setStartInput(state.startDate, true);
              } else if (state.selectPhase === END_DATE) {
                setEndInput(state.endDate, true);
              }

              const nextMonth = getNextActiveMonth(months, numberOfMonths, 1, 1);
              if (isDisabledNextBtn(nextMonth[0].month, nextMonth[0].year)) {
                previousMonthButtonRef.current?.focus();
              }

              showNextMonthA11y.current = true;
              showPreviousMonthA11y.current = false;
            }}
            onKeyDown={(evt) => {
              handleTabForGoIntoCalendar(evt.keyCode === KEY_TAB && !evt.shiftKey && !focusedDate);
              if (evt.keyCode === KEY_SPACE || evt.keyCode === KEY_ENTER) {
                evt.preventDefault();
                nextMonthButtonRef.current?.click();
                refreshA11yMessage();
              }
            }}
          >
            <Icon
              icon="right-chevron"
              size={14}
              fill="text"
              style={{ stroke: '#4a4a4a', strokeWidth: '120px' }}
            />
          </Button>
        </div>
      </div>
    </Inline>
  );

  const content = (
    <Tiles
      columns={isMobile ? 1 : months.length}
      gap="4"
      p="4"
      variant="layout.datepickerStyles.monthContainer"
      onKeyDown={(evt) => {
        if (evt.keyCode === KEY_ESC) {
          pressEscapeKey();
        }
      }}
    >
      {months.map((month, index) => {
        const isSelectedMonth =
          month.month === state.startDate?.getMonth() &&
          month.year === state.startDate.getFullYear();
        return (
          <Month
            ref={isSelectedMonth ? monthRef : undefined}
            dayFormat={dayFormat ?? 'd'}
            key={`${month.year}-${month.month}`}
            year={month.year}
            month={month.month}
            firstDayOfWeek={firstDayOfWeek}
            blackoutDatesIntegration={blackoutDatesIntegration}
            fetchBlackoutDates={fetchBlackoutDates}
            focusedInput={state.focusedInput}
            allowHoverStyleInRange={allowHoverStyleInRange}
            {...rest}
          />
        );
      })}
    </Tiles>
  );

  React.useImperativeHandle(
    ref,
    () => ({
      handleEnterAction: () => {
        overlayState.close();
        if (startRef.current) {
          startRef.current.value = state.startDate ? format(state.startDate, getFormat(false)) : '';
        }
        if (endRef.current) {
          endRef.current.value = state.endDate ? format(state.endDate, getFormat(false)) : '';
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  // Get popover positioning props relative to the trigger
  let { overlayProps: positionProps, updatePosition } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef: popoverRef,
    placement: 'bottom left',
    offset: 0,
    isOpen: overlayState.isOpen,
    shouldFlip
  });

  positionProps = {
    ...positionProps,
    style: {
      ...positionProps.style,
      maxHeight: 'fit-content'
    }
  };

  let overlayTriggerState1 = { ...overlayState, close: updatePosition };
  let { triggerProps } = useOverlayTrigger({ type: 'dialog' }, overlayTriggerState1, triggerRef);

  const newToggleElement = React.cloneElement(inputs, {
    // ...buttonProps,
    ...triggerProps,
    ...inputs.props,
    ref: triggerRef,
    componentReference: triggerRef
  });

  useEffect(() => {
    if (isMobile && isDialogOpen) {
      setMakeClearAction(false);
      setTimeout(() => {
        if (monthRef && monthRef.current) {
          monthRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 250);
    }
  }, [isMobile, isDialogOpen]);

  const inputsToShow = isMobile && isDialogOpen ? null : newToggleElement;

  return (
    <DatePickerProvider
      dateFormat={dateFormat}
      focusedDate={focusedDate}
      selectPhase={state.selectPhase}
      isDateFocused={isDateFocused}
      isDateSelected={isDateSelected}
      isDateHovered={isDateHovered}
      isDateBlocked={isDateBlocked}
      isFirstOrLastSelectedDate={isFirstOrLastSelectedDate}
      onDateSelect={onDateSelect}
      onDateFocus={onDateFocus}
      onDateHover={onDateHover}
      isStartDate={isStartDate}
      isEndDate={isEndDate}
      getMinimumEnabledDate={getMinimumEnabledDate}
    >
      <div ref={ref}>
        {isMobile ? (
          <Fragment>
            {inputsToShow}
            {isDialogOpen && (
              <div onScroll={handleScroll}>
                <MobileFilterDialog
                  label={translate('cmp-datepicker-dialog-title', 'Date Range')}
                  onDismiss={dismiss}
                >
                  <MobileHeader
                    title={translate('cmp-datepicker-header-title', 'Filters')}
                    onBackButtonPress={dismiss}
                  />
                  <Box p="2">{inputs}</Box>
                  <MobileBody>{content}</MobileBody>
                  <MobileFooter align="end">{footer}</MobileFooter>
                </MobileFilterDialog>
              </div>
            )}
          </Fragment>
        ) : (
          <Fragment>
            {newToggleElement}
            {overlayState.isOpen ? (
              <OverlayContainer>
                <Dropdown
                  {...positionProps}
                  popoverRef={popoverRef}
                  activeTab={activeTab}
                  currentTab={currentTab}
                  onClose={() => {
                    setStartInput(state.startDate, false);
                    setEndInput(state.endDate, false);
                    setSelectedDates?.({
                      startDate: state.startDate,
                      endDate: state.endDate
                    });
                    setState((state) =>
                      produce(state, (draft) => {
                        draft.focusedInput = null;
                        draft.selectPhase = null;
                      })
                    );
                    overlayState.close();
                    setActiveDone(false);
                  }}
                >
                  <div>
                    {navigation}
                    {content}
                    {footer}
                  </div>
                </Dropdown>
              </OverlayContainer>
            ) : null}
          </Fragment>
        )}
      </div>
    </DatePickerProvider>
  );
}

const DatePicker = React.forwardRef(DatePickerCmp);

export { DatePicker };
